function adjustScaleForPrecision(size, currentScale, precision) {
  const scaledSize = size * currentScale;
  const targetSize = Math.round(scaledSize / precision) * precision;
  return targetSize / size;
}
function dampenRelative(value, min, max, factor) {
  if (value < min) {
    const overshoot = value - min;
    return min + overshoot * factor / (1 + Math.abs(overshoot) / (max - min));
  } else if (value > max) {
    const overshoot = value - max;
    return max + overshoot * factor / (1 + Math.abs(overshoot) / (max - min));
  }
  return value;
}
function dampen(value, min, max, factor) {
  if (value < min) {
    const overshoot = value - min;
    return min + overshoot * factor;
  } else if (value > max) {
    const overshoot = value - max;
    return max + overshoot * factor;
  }
  return value;
}
function lerp(s, e, t) {
  return s * (1 - t) + e * t;
}
function clamp(a, min = 0, max = 1) {
  return Math.min(max, Math.max(min, a));
}
function calculateCenterPosition(blockingRects, viewport, widthToPlace) {
  const viewportCenterX = (viewport.x + viewport.width) / 2;
  const blockingThreshold = viewport.width / 7;
  const horizontallyConstrainingRects = blockingRects.filter((rect) => {
    const rectLeft = rect.x;
    const rectRight = rect.x + rect.width;
    const viewportLeft = viewport.x;
    const viewportRight = viewport.x + viewport.width;
    const leftSpace = Math.max(0, rectLeft - viewportLeft);
    const rightSpace = Math.max(0, viewportRight - rectRight);
    if (rectRight <= viewportLeft || rectLeft >= viewportRight) {
      return false;
    }
    const overlapLeft = Math.max(rectLeft, viewportLeft);
    const overlapRight = Math.min(rectRight, viewportRight);
    const overlapWidth = Math.max(0, overlapRight - overlapLeft);
    const coverageRatio = overlapWidth / viewport.width;
    if (coverageRatio > 0.85) {
      return false;
    }
    const rectCenterX = (rectLeft + rectRight) / 2;
    const isCentered = Math.abs(rectCenterX - viewportCenterX) < blockingThreshold;
    if (isCentered) {
      const minUsableSpace = Math.max(widthToPlace, viewport.width * 0.2);
      return leftSpace >= minUsableSpace || rightSpace >= minUsableSpace;
    }
    return true;
  });
  const x = horizontallyConstrainingRects.reduce((acc, rect) => {
    if (rect.x < viewportCenterX && viewportCenterX - rect.x > blockingThreshold && rect.x + rect.width > acc) {
      return rect.x + rect.width;
    }
    return acc;
  }, viewport.x);
  const availableWidth = horizontallyConstrainingRects.reduce((acc, rect) => {
    if (rect.x > viewportCenterX && rect.x - viewportCenterX > blockingThreshold && rect.x < acc) {
      return rect.x;
    }
    return acc;
  }, viewport.width + viewport.x);
  const centerX = (x + availableWidth) / 2 - widthToPlace / 2 - viewport.x;
  return { centerX, availableWidth: availableWidth - x };
}
function getMidpoint(touches) {
  const first = touches[0];
  if (!first) {
    throw new Error("Need at least one touch to determine midpoint.");
  }
  const x = first.clientX;
  const y = first.clientY;
  const second = touches[1];
  if (second) {
    return {
      x: (x + second.clientX) / 2,
      y: (y + second.clientY) / 2
    };
  }
  return {
    x,
    y
  };
}
function getDistance(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}
function getTouchDistance(touches) {
  const first = touches[0];
  const second = touches[1];
  if (!first || !second) {
    return 0;
  }
  return getDistance(
    {
      x: first.clientX,
      y: first.clientY
    },
    {
      x: second.clientX,
      y: second.clientY
    }
  );
}
function limitOffset(x, y, boundaries) {
  return {
    x: clamp(x, boundaries.xMin, boundaries.xMax),
    y: clamp(y, boundaries.yMin, boundaries.yMax)
  };
}
function getDirection(a, b, threshold) {
  const angle = Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
  const isHorizontal = angle >= -threshold && angle <= threshold || angle >= 180 - threshold || angle <= -180 + threshold;
  const isVertical = angle >= 90 - threshold && angle <= 90 + threshold || angle >= -90 - threshold && angle <= -90 + threshold;
  if (isHorizontal && !isVertical) {
    return "horizontal";
  } else if (!isHorizontal && isVertical) {
    return "vertical";
  }
  return "both";
}
function getEventCoords(e) {
  if (isTouchEvent(e)) {
    return {
      x: e.touches[0].pageX,
      y: e.touches[0].pageY
    };
  }
  return {
    x: e.pageX,
    y: e.pageY
  };
}
function isTouchEvent(e) {
  return window.TouchEvent && e instanceof TouchEvent;
}
function withPrecision(value, precision) {
  return Math.ceil(value / precision) * precision;
}
function parseOrigin(origin) {
  const [vertical, horizontal] = origin.split("-");
  const x = horizontal === "left" ? 0 : horizontal === "center" ? 0.5 : (
    /* 'right' */
    1
  );
  const y = vertical === "top" ? 0 : vertical === "center" ? 0.5 : (
    /* 'bottom' */
    1
  );
  return { x, y };
}
function withDefault(v, defaultValue) {
  if (v === void 0 || v === null) {
    return defaultValue;
  }
  return Number.isNaN(v) ? defaultValue : v;
}
function parseEdges(v, defaultValue = 0) {
  if (typeof v === "number") {
    return {
      top: v,
      right: v,
      bottom: v,
      left: v
    };
  }
  if (!v) {
    return {
      top: defaultValue,
      right: defaultValue,
      bottom: defaultValue,
      left: defaultValue
    };
  }
  return {
    top: withDefault(v.top, defaultValue),
    right: withDefault(v.right, defaultValue),
    bottom: withDefault(v.bottom, defaultValue),
    left: withDefault(v.left, defaultValue)
  };
}
function asValidNumber(value, defaultValue = 0) {
  if (value === null || value === void 0) {
    return defaultValue;
  }
  if (typeof value === "string") {
    return asValidNumber(parseFloat(value));
  }
  if (Number.isNaN(value)) {
    return defaultValue;
  }
  return value;
}

function easeOutQuad(x) {
  return 1 - (1 - x) * (1 - x);
}
function easeOutBack(x) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
}
function easeOutElastic(x) {
  const c4 = 2 * Math.PI / 3;
  if (x === 0) {
    return 0;
  } else if (x === 1) {
    return 1;
  }
  return Math.pow(2, -10 * x) * Math.sin((x * 10 - 0.75) * c4) + 1;
}
function easeOutCirc(x) {
  return Math.sqrt(1 - Math.pow(x - 1, 2));
}
function easeInOutExpo(x) {
  if (x === 0) {
    return 0;
  } else if (x === 1) {
    return 1;
  }
  return x < 0.5 ? Math.pow(2, 20 * x - 10) / 2 : (2 - Math.pow(2, -20 * x + 10)) / 2;
}
function easeOutCubic(x) {
  return 1 - Math.pow(1 - x, 3);
}
function easeInSine(x) {
  return 1 - Math.cos(x * Math.PI / 2);
}
function easeInOutSine(x) {
  return -(Math.cos(Math.PI * x) - 1) / 2;
}
function easeInOutQuad(x) {
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
}
function easeInOutQuart(x) {
  return x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2;
}
function easeInOutCirc(x) {
  return x < 0.5 ? (1 - Math.sqrt(1 - Math.pow(2 * x, 2))) / 2 : (Math.sqrt(1 - Math.pow(-2 * x + 2, 2)) + 1) / 2;
}
function easeInOutQuint(x) {
  return x < 0.5 ? 16 * x * x * x * x * x : 1 - Math.pow(-2 * x + 2, 5) / 2;
}
function linear(x) {
  return x;
}
const EASINGS = {
  easeInOutExpo,
  easeOutCirc,
  easeOutElastic,
  easeOutQuad,
  easeOutBack,
  easeOutCubic,
  easeInSine,
  easeInOutSine,
  easeInOutQuad,
  easeInOutQuart,
  easeInOutCirc,
  easeInOutQuint,
  linear
};

function applyAnimation(state, currentTime, animation) {
  if (!animation.startTime) {
    animation.startTime = currentTime;
  }
  const elapsedTime = currentTime - animation.startTime;
  const progress = Math.min(elapsedTime / animation.duration, 1);
  const easedProgress = typeof animation.easing === "string" ? EASINGS[animation.easing](progress) : animation.easing(progress);
  const x = lerp(animation.startX, animation.x, easedProgress);
  const y = lerp(animation.startY, animation.y, easedProgress);
  state.offset.x = x;
  state.offset.y = y;
  state.scale = lerp(animation.startScale, animation.scale, easedProgress);
  if (progress >= 1) {
    state.offset.x = animation.x;
    state.offset.y = animation.y;
    state.scale = animation.scale;
    return true;
  }
  return false;
}

function applyMomentum(state, currentTime, boundaries) {
  if (state.momentum === null) {
    return true;
  }
  const deltaTime = (currentTime - state.lastLoopTimestamp) / 1e3;
  if (deltaTime > 0.5) {
    state.momentum = null;
    return true;
  }
  const inLeft = state.offset.x - boundaries.xMin >= -1.5;
  const inRight = state.offset.x - boundaries.xMax <= 1.5;
  const inTop = state.offset.y - boundaries.yMin >= -1.5;
  const inBottom = state.offset.y - boundaries.yMax <= 1.5;
  const deceleration = state.momentum.deceleration;
  const appliedDecelerationX = inLeft && inRight ? deceleration : 0.85;
  const appliedDecelerationY = inTop && inBottom ? deceleration : 0.85;
  state.momentum.x *= appliedDecelerationX;
  state.momentum.y *= appliedDecelerationY;
  const vxDone = Math.abs(state.momentum.x) < 2;
  const vyDone = Math.abs(state.momentum.y) < 2;
  if (!vxDone) {
    state.offset.x = dampen(
      state.offset.x + state.momentum.x * deltaTime,
      boundaries.xMin,
      boundaries.xMax,
      appliedDecelerationX
    );
  }
  if (!vyDone) {
    state.offset.y = dampen(
      state.offset.y + state.momentum.y * deltaTime,
      boundaries.yMin,
      boundaries.yMax,
      appliedDecelerationY
    );
  }
  return vxDone && vyDone && inLeft && inRight && inTop && inBottom;
}
function applyScaleMomentum(state, currentTime, _minScale, maxScale) {
  const deceleration = 0.09;
  const minThreshold = 0.01;
  if (!state.scaleVelocity) {
    return true;
  }
  const deltaTime = (currentTime - state.lastLoopTimestamp) / 1e3;
  if (deltaTime > 0.5) {
    state.scaleVelocity = null;
    return true;
  }
  if (state.scaleVelocity.scale > maxScale) {
    state.scaleVelocity.scale = state.scaleVelocity.scale * 0.95;
  }
  state.offset.x = lerp(state.offset.x, state.scaleVelocity.x, deceleration);
  state.offset.y = lerp(state.offset.y, state.scaleVelocity.y, deceleration);
  state.scale = lerp(state.scale, state.scaleVelocity.scale, deceleration);
  const vxDone = Math.abs(state.scaleVelocity.x - state.offset.x) < minThreshold;
  const vyDone = Math.abs(state.scaleVelocity.y - state.offset.y) < minThreshold;
  const scaleDone = Math.abs(state.scaleVelocity.scale - state.scale) < minThreshold;
  const isDone = vxDone && vyDone && scaleDone;
  if (isDone) {
    state.offset.x = state.scaleVelocity.x;
    state.offset.y = state.scaleVelocity.y;
    state.scale = state.scaleVelocity.scale;
  }
  return isDone;
}

function createOptions(initOptions) {
  const state = {
    options: initOptions || {},
    overscrollBounds: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    }
  };
  function calculateOverscrollBounds() {
    state.overscrollBounds = parseEdges(state.options.overscrollBounds, 30);
  }
  function setAllOptions(newOptions) {
    if (newOptions) {
      state.options = { ...newOptions };
    } else {
      state.options = {};
    }
    calculateOverscrollBounds();
  }
  function set(key, value) {
    state.options[key] = value;
    if (key === "overscrollBounds") {
      calculateOverscrollBounds();
    }
  }
  calculateOverscrollBounds();
  return {
    setAllOptions,
    set,
    get hasBlockingRects() {
      return !!state.options.getBlockingRects;
    },
    get overscrollBounds() {
      return state.overscrollBounds;
    },
    get minScale() {
      return withDefault(state.options.minScale, 0.1);
    },
    get maxScale() {
      return withDefault(state.options.maxScale, 5);
    },
    get scrollStepAmount() {
      return withDefault(state.options.scrollStepAmount, 256);
    },
    get margin() {
      return withDefault(state.options.margin, 20);
    },
    get momentumDeceleration() {
      return withDefault(state.options.momentumDeceleration, 0.96);
    },
    get springDamping() {
      return withDefault(state.options.springDamping, 0.5);
    },
    get direction() {
      return state.options.direction || "both";
    },
    get rootClientRectMaxStale() {
      return withDefault(state.options.rootClientRectMaxStale, 5e3);
    },
    get blockingRects() {
      if (state.options.getBlockingRects) {
        const rects = state.options.getBlockingRects();
        return rects.map((rect) => {
          if (Array.isArray(rect)) {
            return {
              x: rect[0],
              y: rect[1],
              width: rect[2],
              height: rect[3]
            };
          }
          return rect;
        });
      }
      return [];
    }
  };
}

function createArtboard(providedRootEl, initPlugins = [], initOptions = {}) {
  const options = createOptions(initOptions);
  const state = {
    animation: null,
    artboardSize: null,
    interaction: "none",
    lastAnimateToTimestamp: 0,
    lastLoopTimestamp: 0,
    offset: {
      x: asValidNumber(initOptions?.initTransform?.x, 0),
      y: asValidNumber(initOptions?.initTransform?.y, 0)
    },
    rootRect: providedRootEl.getBoundingClientRect(),
    rootSize: {
      width: providedRootEl.offsetWidth,
      height: providedRootEl.offsetHeight
    },
    scale: asValidNumber(initOptions?.initTransform?.scale, 1),
    touchDirection: "none",
    momentum: null,
    momentumStopTimestamp: 0,
    scaleVelocity: null
  };
  const rootEl = providedRootEl;
  let plugins = [];
  let lastRootRectUpdate = 0;
  function handleResize(entries) {
    for (const entry of entries) {
      if (entry.target === rootEl) {
        if (entry.target instanceof HTMLElement) {
          updateRootRect(true);
        }
        const size = entry.contentBoxSize[0];
        if (!size) {
          return;
        }
        state.rootSize.width = size.inlineSize;
        state.rootSize.height = size.blockSize;
      } else {
        const cb = sizeObserverMap.get(entry.target);
        if (cb) {
          cb(entry);
        }
      }
    }
  }
  const sizeObserverMap = /* @__PURE__ */ new WeakMap();
  let resizeTimeout = null;
  let init = true;
  const resizeObserver = new ResizeObserver(function(entries) {
    if (init) {
      init = false;
      handleResize(entries);
      return;
    }
    if (resizeTimeout) {
      window.clearTimeout(resizeTimeout);
    }
    resizeTimeout = window.setTimeout(function() {
      handleResize(entries);
    }, 300);
  });
  function observeSizeChange(element, cb) {
    if (sizeObserverMap.has(element)) {
      throw new Error("An observer for this element has already been added.");
    }
    sizeObserverMap.set(element, cb);
    resizeObserver.observe(element);
    return {
      unobserve: () => {
        sizeObserverMap.delete(element);
        resizeObserver.unobserve(element);
      }
    };
  }
  function setOptions(newOptions) {
    options.setAllOptions(newOptions);
  }
  function setOption(key, value) {
    options.set(key, value);
  }
  function getScale() {
    return state.scale;
  }
  function getFinalScale() {
    if (state.animation) {
      return state.animation.scale;
    } else if (state.scaleVelocity) {
      return state.scaleVelocity.scale;
    }
    return state.scale;
  }
  function getOffset() {
    return { ...state.offset };
  }
  function getFinalOffset() {
    if (state.animation) {
      return {
        x: state.animation.x,
        y: state.animation.y
      };
    }
    return { ...state.offset };
  }
  function destroy() {
    plugins.forEach((plugin) => {
      if (plugin.destroy) {
        plugin.destroy();
      }
    });
    resizeObserver.disconnect();
  }
  function animateToBoundary() {
    const boundaries = getBoundaries();
    const targetX = clamp(state.offset.x, boundaries.xMin, boundaries.xMax);
    const targetY = clamp(state.offset.y, boundaries.yMin, boundaries.yMax);
    if (Math.abs(targetX - state.offset.x) > 0.1 || Math.abs(targetY - state.offset.y) > 0.1) {
      setMomentum(state.offset.x - targetX, state.offset.y - targetY);
      setInteraction("momentum");
    }
  }
  function stopMomentum() {
    animateToBoundary();
    setInteraction();
    setTouchDirection();
    state.momentumStopTimestamp = performance.now();
    state.momentum = null;
    state.scaleVelocity = null;
    state.animation = null;
  }
  function loop(currentTime) {
    if (!state.lastLoopTimestamp) {
      state.lastLoopTimestamp = currentTime;
    }
    const boundaries = getBoundaries();
    if (state.interaction === "momentum") {
      const shouldStop = applyMomentum(state, currentTime, boundaries);
      if (shouldStop) {
        stopMomentum();
      }
    } else if (state.interaction === "momentumScaling") {
      const shouldStop = applyScaleMomentum(
        state,
        currentTime,
        options.minScale,
        options.maxScale
      );
      if (shouldStop) {
        stopMomentum();
      }
    } else if (state.animation) {
      const isFinished = applyAnimation(state, currentTime, state.animation);
      if (isFinished) {
        state.animation = null;
      }
    }
    const ctx = {
      rootSize: {
        ...state.rootSize
      },
      artboardSize: state.artboardSize ? {
        ...state.artboardSize
      } : null,
      offset: {
        ...state.offset
      },
      scale: state.scale,
      boundaries,
      currentTime
    };
    for (let i = 0; i < plugins.length; i++) {
      const plugin = plugins[i];
      if (plugin && plugin.loop) {
        plugin.loop(ctx);
      }
    }
    state.lastLoopTimestamp = currentTime;
    return ctx;
  }
  function getCenterX(targetScale) {
    if (!state.artboardSize) {
      return state.offset.x;
    }
    const scaleToUse = targetScale ?? state.scale;
    const blockingRects = options.blockingRects;
    return calculateCenterPosition(
      blockingRects,
      rootEl.getBoundingClientRect(),
      state.artboardSize.width * scaleToUse
    ).centerX;
  }
  function animateTo(key, targetX, targetY, targetScale, animationOptions) {
    setInteraction("none");
    const direction = options.direction;
    const x = direction === "both" || direction === "horizontal" ? targetX : getCenterX(targetScale);
    const y = direction === "both" || direction === "vertical" ? targetY : 0;
    state.animation = {
      key,
      x,
      y,
      scale: targetScale || state.scale,
      startX: state.offset.x,
      startY: state.offset.y,
      startScale: state.scale,
      easing: animationOptions?.easing || "easeOutCubic",
      duration: animationOptions?.duration || 400,
      startTime: 0
    };
    state.momentum = null;
    state.scaleVelocity = null;
  }
  function getArtboardSize() {
    if (!state.artboardSize) {
      return null;
    }
    return {
      width: state.artboardSize.width,
      height: state.artboardSize.height
    };
  }
  function getRootSize() {
    return {
      width: state.rootSize.width,
      height: state.rootSize.height
    };
  }
  function animateOrJumpBy(providedX, providedY, options2) {
    const x = typeof providedX === "number" ? providedX : 0;
    const y = typeof providedY === "number" ? providedY : 0;
    const diff = performance.now() - state.lastAnimateToTimestamp;
    if (diff < 300) {
      setOffset(
        (state.animation?.x || state.offset.x) + x,
        (state.animation?.y || state.offset.y) + y,
        true
      );
      state.animation = null;
    } else {
      const limited = limitOffset(
        state.offset.x + x,
        state.offset.y + y,
        getBoundaries()
      );
      animateTo("animateOrJumpBy", limited.x, limited.y, state.scale, options2);
    }
    state.lastAnimateToTimestamp = performance.now();
  }
  function animateOrJumpTo(providedX, providedY, options2) {
    const x = typeof providedX === "number" ? providedX : state.offset.x;
    const y = typeof providedY === "number" ? providedY : state.offset.y;
    const diff = performance.now() - state.lastAnimateToTimestamp;
    if (diff < 300) {
      setOffset(x, y, true);
      state.animation = null;
    } else {
      animateTo("animateOrJumpTo", x, y, state.scale, options2);
    }
    state.lastAnimateToTimestamp = performance.now();
  }
  function scrollPageUp(o) {
    animateOrJumpBy(0, state.rootSize.height, o);
  }
  function scrollPageDown(o) {
    animateOrJumpBy(0, -state.rootSize.height, o);
  }
  function scrollPageLeft(o) {
    animateOrJumpBy(state.rootSize.width, null, o);
  }
  function scrollPageRight(o) {
    animateOrJumpBy(-state.rootSize.width, null, o);
  }
  function scrollUp(amount, o) {
    animateOrJumpBy(0, amount || options.scrollStepAmount, o);
  }
  function scrollDown(amount, o) {
    animateOrJumpBy(0, amount || -options.scrollStepAmount, o);
  }
  function scrollLeft(amount, o) {
    animateOrJumpBy(amount || options.scrollStepAmount, null, o);
  }
  function scrollRight(amount, o) {
    animateOrJumpBy(amount || -options.scrollStepAmount, null, o);
  }
  function scrollToTop(o) {
    animateOrJumpTo(null, options.margin, o);
  }
  function scrollToEnd(o) {
    if (!state.artboardSize) {
      return;
    }
    const v = state.artboardSize.height * state.scale;
    const y = -v + state.rootSize.height - options.margin;
    animateOrJumpTo(null, y, o);
  }
  function scaleToFit(scrollOptions) {
    if (!state.artboardSize) {
      return;
    }
    scrollIntoView(
      {
        x: 0,
        y: 0,
        width: state.artboardSize.width,
        height: state.artboardSize.height
      },
      {
        scale: "blocking",
        ...scrollOptions || {}
      }
    );
  }
  function resetZoom(o) {
    if (!state.artboardSize) {
      return;
    }
    const animationOptions = {
      duration: o?.duration || 300,
      easing: o?.easing
    };
    const viewportCenterY = state.rootSize.height / 2;
    const currentCenterOnArtboard = (-state.offset.y + viewportCenterY) / state.scale;
    if (state.artboardSize.height < state.rootSize.height) {
      const newYOffset2 = state.rootSize.height / 2 - state.artboardSize.height / 2;
      return animateTo(
        "resetZoom",
        getCenterX(1),
        newYOffset2,
        1,
        animationOptions
      );
    }
    const newYOffset = Math.min(
      Math.max(
        -currentCenterOnArtboard + viewportCenterY,
        -state.artboardSize.height + state.rootSize.height - options.overscrollBounds.top
      ),
      options.overscrollBounds.top + options.overscrollBounds.bottom
    );
    animateTo("resetZoom", getCenterX(1), newYOffset, 1, animationOptions);
  }
  function getBoundaries(providedTargetScale) {
    if (!state.artboardSize) {
      return {
        xMin: Number.NEGATIVE_INFINITY,
        xMax: Number.POSITIVE_INFINITY,
        yMin: Number.NEGATIVE_INFINITY,
        yMax: Number.POSITIVE_INFINITY
      };
    }
    const targetScale = providedTargetScale || state.scale;
    const artboardWidth = state.artboardSize.width * targetScale;
    const artboardHeight = state.artboardSize.height * targetScale;
    const bounds = options.overscrollBounds;
    const paddingTop = Math.min(bounds.top, state.rootSize.height / 4);
    const paddingBottom = Math.min(bounds.bottom, state.rootSize.height / 4);
    const paddingLeft = Math.min(bounds.left, state.rootSize.width / 4);
    const paddingRight = Math.min(bounds.right, state.rootSize.width / 4);
    const xMin = -artboardWidth + paddingLeft;
    const xMax = state.rootSize.width - paddingRight;
    const yMin = -artboardHeight + paddingTop;
    const yMax = state.rootSize.height - paddingBottom;
    return { xMin, xMax, yMin, yMax };
  }
  function constrainScale(scale) {
    return clamp(scale, options.minScale, options.maxScale);
  }
  function setScale(newScale, immediate) {
    if (immediate) {
      state.scale = constrainScale(newScale);
      return;
    }
    state.scale = dampenRelative(
      newScale,
      options.minScale,
      options.maxScale,
      0.9
    );
  }
  function zoomIn(delta = 10) {
    doZoom(state.rootSize.width / 2, state.rootSize.height / 2, delta);
  }
  function zoomOut(delta = -10) {
    doZoom(state.rootSize.width / 2, state.rootSize.height / 2, delta);
  }
  function setOffset(providedX, providedY, immediate) {
    const direction = options.direction;
    const setX = direction === "both" || direction === "horizontal";
    const setY = direction === "both" || direction === "vertical";
    const x = typeof providedX === "number" && setX ? providedX : state.offset.x;
    const y = typeof providedY === "number" && setY ? providedY : state.offset.y;
    const boundaries = getBoundaries();
    if (immediate) {
      const limited = limitOffset(x, y, boundaries);
      state.offset.x = limited.x;
      state.offset.y = limited.y;
      return;
    }
    state.offset.x = dampenRelative(
      x,
      boundaries.xMin,
      boundaries.xMax,
      options.springDamping
    );
    state.offset.y = dampenRelative(
      y,
      boundaries.yMin,
      boundaries.yMax,
      options.springDamping
    );
  }
  function cancelAnimation() {
    state.animation = null;
    state.momentum = null;
  }
  function updateRootRect(force) {
    const now = performance.now();
    if (force || now - lastRootRectUpdate > options.rootClientRectMaxStale) {
      state.rootRect = rootEl.getBoundingClientRect();
      lastRootRectUpdate = now;
    }
  }
  function calculateScaleAroundPoint(pageX, pageY, targetScale, providedOffset, providedScale) {
    updateRootRect();
    const newScale = constrainScale(targetScale);
    const offset = providedOffset || getOffset();
    const scale = providedScale || getScale();
    const x = pageX - state.rootRect.x;
    const y = pageY - state.rootRect.y;
    const transformedX = (x - offset.x) / scale;
    const transformedY = (y - offset.y) / scale;
    const targetX = -transformedX * newScale + x;
    const targetY = -transformedY * newScale + y;
    const limited = limitOffset(targetX, targetY, getBoundaries(newScale));
    return {
      x: limited.x,
      y: limited.y,
      scale: newScale
    };
  }
  function scaleAroundPoint(pageX, pageY, targetScale, animationOptions) {
    updateRootRect();
    const newScale = constrainScale(targetScale);
    const x = pageX - state.rootRect.x;
    const y = pageY - state.rootRect.y;
    const transformedX = (x - state.offset.x) / state.scale;
    const transformedY = (y - state.offset.y) / state.scale;
    const targetX = -transformedX * newScale + x;
    const targetY = -transformedY * newScale + y;
    const limited = limitOffset(targetX, targetY, getBoundaries(newScale));
    if (animationOptions) {
      animateTo(
        "scaleAroundPoint",
        limited.x,
        limited.y,
        newScale,
        typeof animationOptions === "object" ? animationOptions : null
      );
      return;
    }
    setScale(newScale, true);
    setOffset(limited.x, limited.y, true);
  }
  function doZoom(x, y, delta) {
    const scaleFactor = Math.pow(1.5, Math.sign(delta) / 2);
    const newScale = state.scale * scaleFactor;
    scaleAroundPoint(x, y, newScale);
  }
  function scrollIntoView(targetRect, animationOptions) {
    const targetWidth = targetRect.width;
    const targetHeight = targetRect.height;
    const targetX = targetRect.x;
    const targetY = targetRect.y;
    const scaleOption = animationOptions?.scale || "none";
    const targetScale = (() => {
      if (scaleOption === "full") {
        const scaleX = (state.rootSize.width - options.margin * 2) / targetWidth;
        const scaleY = (state.rootSize.height - options.margin * 2) / targetHeight;
        return Math.min(scaleX, scaleY, options.maxScale);
      } else if (scaleOption === "blocking" && options.hasBlockingRects) {
        const { availableWidth } = calculateCenterPosition(
          options.blockingRects,
          rootEl.getBoundingClientRect(),
          targetWidth
        );
        const targetScale2 = (availableWidth - options.margin * 2) / targetWidth;
        const scaleY = (state.rootSize.height - options.margin * 2) / targetHeight;
        return Math.min(targetScale2, scaleY, options.maxScale);
      }
      return 1;
    })();
    const behavior = animationOptions?.behavior || "auto";
    const axis = animationOptions?.axis || "both";
    const scrollX = axis === "x" || axis === "both";
    const scrollY = axis === "y" || axis === "both";
    const centeredOffsetX = (() => {
      if (scaleOption === "blocking") {
        return getCenterX(targetScale);
      }
      return scrollX ? -(targetX * targetScale) + (state.rootSize.width - targetWidth * targetScale) / 2 : state.offset.x;
    })();
    const centeredOffsetY = scrollY ? -(targetY * targetScale) + (state.rootSize.height - targetHeight * targetScale) / 2 : state.offset.y;
    if (behavior === "smooth" || behavior === "auto" && !state.animation) {
      animateTo(
        "scrollIntoView",
        centeredOffsetX,
        centeredOffsetY,
        targetScale,
        {
          duration: animationOptions?.duration || 300,
          easing: animationOptions?.easing || "easeInOutExpo"
        }
      );
      return;
    }
    cancelAnimation();
    setOffset(centeredOffsetX, centeredOffsetY, true);
    setScale(targetScale, true);
  }
  function setArtboardSize(width, height) {
    if (!state.artboardSize) {
      state.artboardSize = {
        width: 0,
        height: 0
      };
    }
    state.artboardSize.width = width;
    state.artboardSize.height = height;
  }
  function getRootElement() {
    return rootEl;
  }
  function getInteraction() {
    return state.interaction;
  }
  function setInteraction(v) {
    const targetInteraction = v || "none";
    if (state.interaction === targetInteraction) {
      return;
    }
    if ((state.interaction === "momentum" || state.interaction === "momentumScaling") && targetInteraction !== "momentum" && targetInteraction !== "momentumScaling") {
      state.momentumStopTimestamp = performance.now();
    }
    state.interaction = targetInteraction;
  }
  function getMomentum() {
    if (state.momentum) {
      return {
        ...state.momentum
      };
    }
    return null;
  }
  function setMomentum(momentumX, momentumY, deceleration) {
    if (momentumX !== void 0 && momentumX !== null && momentumY !== void 0) {
      const direction = options.direction;
      const x = direction === "both" || direction === "horizontal" ? momentumX : 0;
      const y = direction === "both" || direction === "vertical" ? momentumY : 0;
      state.momentum = {
        x,
        y,
        deceleration: deceleration || options.momentumDeceleration
      };
    } else {
      state.momentum = null;
    }
  }
  function getTouchDirection() {
    return state.touchDirection;
  }
  function setTouchDirection(v) {
    state.touchDirection = v || "none";
  }
  function startMomentum(velocity) {
    const totalVelocity = velocity ? Math.abs(velocity.x) + Math.abs(velocity.y) : null;
    if (!velocity || !totalVelocity) {
      state.momentum = null;
      setInteraction("none");
      animateToBoundary();
      return;
    }
    const x = state.touchDirection === "horizontal" || state.touchDirection === "both" ? velocity.x : 0;
    const y = state.touchDirection === "vertical" || state.touchDirection === "both" ? velocity.y : 0;
    setMomentum(x, y);
    setInteraction("momentum");
  }
  function setDirectionOffset(x, y) {
    if (state.touchDirection === "both" || state.touchDirection === "none") {
      setOffset(x, y);
    } else if (state.touchDirection === "vertical") {
      setOffset(state.offset.x, y);
    } else if (state.touchDirection === "horizontal") {
      setOffset(x, state.offset.y);
    }
  }
  function setScaleTarget(newX, newY, scale) {
    const direction = options.direction;
    const x = direction === "both" || direction === "horizontal" ? newX : getCenterX();
    const y = direction === "both" || direction === "vertical" ? newY : 0;
    if (state.scaleVelocity) {
      state.scaleVelocity.scale = scale;
      state.scaleVelocity.x = x;
      state.scaleVelocity.y = y;
    } else {
      state.scaleVelocity = {
        x,
        y,
        scale
      };
    }
  }
  function getScaleTarget() {
    if (state.scaleVelocity) {
      return {
        ...state.scaleVelocity
      };
    }
    return null;
  }
  function scrollElementIntoView(targetEl, options2) {
    const targetBoundingRect = targetEl.getBoundingClientRect();
    const targetWidth = targetEl.offsetWidth;
    const targetHeight = targetEl.offsetHeight;
    const scale = getScale();
    const targetX = (targetBoundingRect.x - state.offset.x - state.rootRect.x) / scale;
    const targetY = (targetBoundingRect.y - state.offset.y - state.rootRect.y) / scale;
    scrollIntoView(
      {
        width: targetWidth,
        height: targetHeight,
        x: targetX,
        y: targetY
      },
      options2
    );
  }
  function getAnimation() {
    if (state.animation) {
      return { ...state.animation };
    }
    return null;
  }
  function wasMomentumScrolling() {
    return performance.now() - state.momentumStopTimestamp < 200;
  }
  const artboard = {
    observeSizeChange,
    wasMomentumScrolling,
    addPlugin,
    animateOrJumpBy,
    animateOrJumpTo,
    animateTo,
    animateToBoundary,
    startMomentum,
    cancelAnimation,
    destroy,
    getArtboardSize,
    getCenterX,
    getFinalOffset,
    getFinalScale,
    getInteraction,
    getOffset,
    getRootElement,
    getRootSize,
    getScale,
    getTouchDirection,
    getMomentum,
    getAnimation,
    loop,
    options,
    removePlugin,
    resetZoom,
    scaleAroundPoint,
    scaleToFit,
    scrollDown,
    scrollElementIntoView,
    scrollIntoView,
    scrollLeft,
    scrollPageDown,
    scrollPageLeft,
    scrollPageRight,
    scrollPageUp,
    scrollRight,
    scrollToEnd,
    scrollToTop,
    scrollUp,
    setArtboardSize,
    setDirectionOffset,
    setInteraction,
    setOffset,
    setOption,
    setOptions,
    setScale,
    setTouchDirection,
    setMomentum,
    zoomIn,
    zoomOut,
    getBoundaries,
    setScaleTarget,
    calculateScaleAroundPoint,
    getScaleTarget
  };
  function addPlugin(plugin) {
    const pluginInstance = plugin.init(artboard, plugin.options);
    plugins.push(pluginInstance);
    return pluginInstance;
  }
  function removePlugin(plugin) {
    if (plugin.destroy) {
      plugin.destroy();
    }
    plugins = plugins.filter((v) => v !== plugin);
  }
  resizeObserver.observe(rootEl);
  if (initPlugins?.length) {
    initPlugins.forEach((plugin) => addPlugin(plugin));
  }
  return artboard;
}

function inlineStyleOverrider(element) {
  const overridenStyles = /* @__PURE__ */ new Map();
  const overridenProperties = /* @__PURE__ */ new Map();
  const prevValues = /* @__PURE__ */ new Map();
  function set(property, value) {
    const prev = prevValues.get(property);
    if (prev === value) {
      return;
    }
    if (prev === void 0) {
      overridenStyles.set(property, element.style[property] ?? "");
    }
    element.style[property] = typeof value === "number" ? value + "px" : value;
    prevValues.set(property, value);
  }
  function setProperty(property, value) {
    const prev = prevValues.get(property);
    if (prev === value) {
      return;
    }
    if (prev === void 0) {
      overridenProperties.set(
        property,
        element.style.getPropertyValue(property)
      );
    }
    const propertyValue = typeof value === "number" ? value + "px" : value;
    element.style.setProperty(property, propertyValue);
    prevValues.set(property, value);
  }
  function setTransform(x, y, scale, scaleY) {
    let transform = `translate3d(${x}px, ${y}px, 0px)`;
    if (scale) {
      if (scaleY) {
        transform += ` scale(${scale}, ${scaleY})`;
      } else {
        transform += ` scale(${scale})`;
      }
    }
    set("transform", transform);
  }
  function setMultiple(styles) {
    Object.entries(styles).forEach(([property, value]) => {
      if (value !== void 0) {
        set(property, value);
      }
    });
  }
  function restore() {
    try {
      const styleEntries = [...overridenStyles.entries()];
      styleEntries.forEach(([property, value]) => {
        element.style[property] = value;
      });
      const propertyEntries = [...overridenProperties.entries()];
      propertyEntries.forEach(([property, value]) => {
        element.style.setProperty(property, value);
      });
    } catch {
    }
  }
  return {
    set,
    setTransform,
    setProperty,
    setMultiple,
    restore
  };
}

function pluginOptions(providedOptions) {
  let options = providedOptions;
  const computedCache = /* @__PURE__ */ new Map();
  function get(key, defaultValue) {
    const v = options ? options[key] : void 0;
    if (v === void 0 && defaultValue !== void 0) {
      return defaultValue;
    }
    if (typeof v === "number" && Number.isNaN(v)) {
      return defaultValue;
    }
    return v;
  }
  function getRequired(key) {
    const value = get(key);
    if (value === void 0 || value === null) {
      throw new Error(`Missing required plugin option "${String(key)}".`);
    }
    return value;
  }
  function getElement(key, fallbackSelector, parent) {
    const value = get(key);
    if (value instanceof HTMLElement) {
      return value;
    } else if (typeof value === "string") {
      const possibleElement = parent.querySelector(value);
      if (possibleElement instanceof HTMLElement) {
        return possibleElement;
      }
    } else {
      const possibleElement = parent.querySelector(fallbackSelector);
      if (possibleElement instanceof HTMLElement) {
        return possibleElement;
      }
    }
    throw new Error(`Failed to locate element for "${String(key)} option."`);
  }
  function should(key, defaultValue) {
    const v = options ? options[key] : void 0;
    if (v === void 0 && defaultValue !== void 0) {
      return !!defaultValue;
    }
    return !!v;
  }
  function recompute() {
    computedCache.forEach((cacheEntry, callback) => {
      cacheEntry.value = callback(options);
    });
  }
  function set(key, value) {
    if (!options) {
      options = {};
    }
    options[key] = value;
    recompute();
  }
  function setAll(newOptions) {
    options = newOptions;
    recompute();
  }
  function computed(callback) {
    if (computedCache.has(callback)) {
      return computedCache.get(callback);
    }
    const initialValue = callback(options);
    const cacheEntry = { value: initialValue };
    computedCache.set(callback, cacheEntry);
    return cacheEntry;
  }
  return {
    get,
    getRequired,
    getElement,
    should,
    set,
    setAll,
    computed
  };
}

function defineArtboardPlugin(init) {
  return function(providedOptions) {
    const options = pluginOptions(providedOptions);
    return {
      options,
      init
    };
  };
}

const clickZoom = defineArtboardPlugin(function(artboard, options) {
  const rootEl = artboard.getRootElement();
  let mouseStartCoords = null;
  function shouldZoom() {
    return !artboard.wasMomentumScrolling();
  }
  function onPointerDown(e) {
    if (e.pointerType === "touch") {
      destroy();
      return;
    }
    mouseStartCoords = getEventCoords(e);
  }
  function onPointerUp(e) {
    if (!mouseStartCoords) {
      return;
    }
    const distance = getDistance(mouseStartCoords, getEventCoords(e));
    if (distance > 10) {
      return;
    }
    if (!shouldZoom()) {
      return;
    }
    doZoom(mouseStartCoords);
  }
  function getTargetScale() {
    const maxScale = artboard.options.maxScale;
    const threshold = maxScale * 0.5;
    const currentScale = artboard.getFinalScale();
    if (currentScale < 1) {
      return 1;
    }
    return currentScale >= threshold ? 1 : maxScale;
  }
  function doZoom(coords) {
    const animation = options.get("animation", {
      duration: 500,
      easing: "easeInOutExpo"
    });
    artboard.scaleAroundPoint(coords.x, coords.y, getTargetScale(), animation);
  }
  function destroy() {
    rootEl.removeEventListener("pointerdown", onPointerDown);
    rootEl.removeEventListener("pointerup", onPointerUp);
  }
  rootEl.addEventListener("pointerdown", onPointerDown);
  rootEl.addEventListener("pointerup", onPointerUp);
  return {
    options,
    destroy
  };
});

const cssProperties = defineArtboardPlugin(function(artboard, options) {
  const element = options.get("element") || artboard.getRootElement();
  const style = inlineStyleOverrider(element);
  function getValue(v, precision) {
    if (options.should("unitless")) {
      return withPrecision(v, precision).toString();
    }
    return withPrecision(v, precision);
  }
  const properties = options.computed(
    function(o) {
      return Object.fromEntries(o.properties.map((v) => [v, true]));
    }
  );
  function loop(ctx) {
    const precision = options.get("precision", 0.5);
    if (properties.value["--artboard-offset-x"]) {
      const x = getValue(ctx.offset.x, precision);
      style.setProperty("--artboard-offset-x", x);
    }
    if (properties.value["--artboard-offset-y"]) {
      const y = getValue(ctx.offset.y, precision);
      style.setProperty("--artboard-offset-y", y);
    }
    if (properties.value["--artboard-size-width"]) {
      const width = getValue(ctx.artboardSize?.width || 0, precision);
      style.setProperty("--artboard-size-width", width);
    }
    if (properties.value["--artboard-size-height"]) {
      const height = getValue(ctx.artboardSize?.height || 0, precision);
      style.setProperty("--artboard-size-height", height);
    }
    if (properties.value["--artboard-root-width"]) {
      const width = getValue(ctx.rootSize.width, precision);
      style.setProperty("--artboard-root-width", width);
    }
    if (properties.value["--artboard-root-height"]) {
      const height = getValue(ctx.rootSize.height, precision);
      style.setProperty("--artboard-root-height", height);
    }
    if (properties.value["--artboard-scale"]) {
      style.setProperty("--artboard-scale", ctx.scale.toString());
    }
  }
  function destroy() {
    if (options.should("restoreProperties")) {
      style.restore();
    }
  }
  return {
    options,
    destroy,
    loop
  };
});

const dom = defineArtboardPlugin(function(artboard, options) {
  const artboardElement = options.getRequired("element");
  const styleOverrider = inlineStyleOverrider(artboardElement);
  function loop(ctx) {
    if (!ctx.artboardSize) {
      return;
    }
    const precision = options.get("precision", 0.5);
    const x = withPrecision(ctx.offset.x, precision);
    const y = withPrecision(ctx.offset.y, precision);
    if (options.should("applyScalePrecision")) {
      const scaleX = adjustScaleForPrecision(
        ctx.artboardSize.width,
        ctx.scale,
        precision
      );
      const scaleY = adjustScaleForPrecision(
        ctx.artboardSize.height,
        ctx.scale,
        precision
      );
      styleOverrider.setTransform(x, y, scaleX, scaleY);
    } else {
      styleOverrider.setTransform(x, y, ctx.scale);
    }
  }
  const { unobserve } = artboard.observeSizeChange(artboardElement, (entry) => {
    const size = entry.contentBoxSize[0];
    if (!size) {
      return;
    }
    if (entry.target instanceof HTMLImageElement) {
      artboard.setArtboardSize(
        entry.target.naturalWidth,
        entry.target.naturalHeight
      );
      styleOverrider.set("width", entry.target.naturalWidth);
      styleOverrider.set("height", entry.target.naturalHeight);
      return;
    }
    artboard.setArtboardSize(size.inlineSize, size.blockSize);
  });
  function applyInitStyles() {
    styleOverrider.setMultiple({
      position: "absolute",
      top: "0px",
      left: "0px",
      transformOrigin: "0 0"
    });
  }
  function onImageLoaded() {
    if (artboardElement instanceof HTMLImageElement) {
      artboard.setArtboardSize(
        artboardElement.naturalWidth,
        artboardElement.naturalHeight
      );
      if (options.should("setInitTransformFromRect")) {
        setInitTransformFromRect();
      }
      styleOverrider.set("width", artboardElement.naturalWidth);
      styleOverrider.set("height", artboardElement.naturalHeight);
      styleOverrider.set("maxWidth", "none");
      applyInitStyles();
    }
  }
  function setInitTransformFromRect() {
    const artboardRect = artboardElement.getBoundingClientRect();
    const rootRect = artboard.getRootElement().getBoundingClientRect();
    const x = artboardRect.x - rootRect.x;
    const y = artboardRect.y - rootRect.y;
    const artboardWidth = artboardElement instanceof HTMLImageElement ? artboardElement.naturalWidth : artboardElement.offsetWidth;
    const scale = Math.round(artboardRect.width / artboardWidth * 1e3) / 1e3;
    artboard.setScale(scale, true);
    artboard.setOffset(x, y, true);
  }
  if (artboardElement instanceof HTMLImageElement) {
    if (artboardElement.complete) {
      onImageLoaded();
    } else {
      artboardElement.addEventListener("load", onImageLoaded);
    }
  } else {
    artboard.setArtboardSize(
      artboardElement.offsetWidth,
      artboardElement.offsetHeight
    );
    if (options.should("setInitTransformFromRect")) {
      setInitTransformFromRect();
    }
    applyInitStyles();
  }
  function destroy() {
    unobserve();
    artboardElement.removeEventListener("load", onImageLoaded);
    if (options.should("restoreStyles")) {
      styleOverrider.restore();
    }
  }
  return {
    options,
    loop,
    destroy
  };
});

const doubleTapZoom = defineArtboardPlugin(function(artboard, options) {
  const rootEl = artboard.getRootElement();
  let startTime = 0;
  let startOffset = null;
  function onTouchStart(e) {
    if (e.touches.length !== 1) {
      return;
    }
    const duration = e.timeStamp - startTime;
    if (duration < 300 && startOffset) {
      const distance = getDistance(startOffset, artboard.getOffset());
      if (distance < 10) {
        doZoom(getEventCoords(e));
        return;
      }
    }
    startTime = e.timeStamp;
    startOffset = artboard.getOffset();
  }
  function doZoom(coords) {
    const currentScale = artboard.getFinalScale();
    const targetScale = currentScale >= 3 ? 1 : 6;
    const animation = options.get("animation", {
      duration: 500,
      easing: "easeInOutExpo"
    });
    artboard.scaleAroundPoint(coords.x, coords.y, targetScale, animation);
  }
  function destroy() {
    rootEl.removeEventListener("touchstart", onTouchStart);
  }
  rootEl.addEventListener("touchstart", onTouchStart);
  return {
    options,
    destroy
  };
});

const DEFAULT_KEYMAP = {
  ArrowDown: ["scrollDown"],
  ArrowUp: ["scrollUp"],
  ArrowLeft: ["scrollLeft"],
  ArrowRight: ["scrollRight"],
  Home: ["scrollToTop"],
  End: ["scrollToEnd"],
  PageUp: ["scrollPageUp"],
  PageDown: ["scrollPageDown"],
  Digit0: ["resetZoom", true],
  Digit1: ["scaleToFit", true]
};
const keyboard = defineArtboardPlugin(function(artboard, options) {
  document.addEventListener("keydown", onKeyDown);
  function destroy() {
    document.removeEventListener("keydown", onKeyDown);
  }
  function isPressingModifier(e) {
    const modifier = options.get("modifier", "ctrlmeta");
    if (modifier === "ctrl") {
      return e.ctrlKey;
    } else if (modifier === "alt") {
      return e.altKey;
    } else if (modifier === "meta") {
      return e.metaKey;
    } else if (modifier === "ctrlmeta") {
      return e.metaKey || e.ctrlKey;
    }
    throw new Error("Invalid modifier key.");
  }
  function onKeyDown(e) {
    const keymap = options.get("keymap", DEFAULT_KEYMAP);
    if (!artboard) {
      return;
    }
    const mapping = keymap[e.code];
    if (!mapping) {
      return;
    }
    const method = mapping[0];
    const needsModifier = !!mapping[1];
    if (needsModifier && !isPressingModifier(e)) {
      return;
    }
    e.preventDefault();
    artboard[method]();
  }
  return {
    options,
    destroy
  };
});

function createQueue() {
  const queue = [];
  const timeQueue = [];
  function reset() {
    queue.splice(0);
    timeQueue.splice(0);
  }
  function add(position) {
    queue.push(position);
    timeQueue.push(performance.now());
  }
  return {
    reset,
    add,
    queue,
    timeQueue
  };
}
function createDirectionQueue(options) {
  const queue = createQueue();
  function getDirection(force) {
    if (queue.queue.length < 2) {
      return void 0;
    }
    const timeDiff = queue.timeQueue[queue.timeQueue.length - 1] - queue.timeQueue[0];
    if (timeDiff > 50 || queue.queue.length > 5 || force) {
      let totalDeltaX = 0;
      let totalDeltaY = 0;
      for (let i = 1; i < queue.queue.length; i++) {
        totalDeltaX += queue.queue[i].x - queue.queue[i - 1].x;
        totalDeltaY += queue.queue[i].y - queue.queue[i - 1].y;
      }
      const avgDeltaX = totalDeltaX / (queue.queue.length - 1);
      const avgDeltaY = totalDeltaY / (queue.queue.length - 1);
      const absAvgDeltaX = Math.abs(avgDeltaX);
      const absAvgDeltaY = Math.abs(avgDeltaY);
      const angle = Math.atan2(absAvgDeltaY, absAvgDeltaX) * 180 / Math.PI;
      const isHorizontal = angle >= -options.threshold && angle <= options.threshold || angle >= 180 - options.threshold || angle <= -180 + options.threshold;
      const isVertical = angle >= 90 - options.threshold && angle <= 90 + options.threshold || angle >= -90 - options.threshold && angle <= -90 + options.threshold;
      if (isHorizontal && !isVertical) {
        return "horizontal";
      } else if (!isHorizontal && isVertical) {
        return "vertical";
      }
      return "both";
    }
    return void 0;
  }
  function add(v) {
    queue.add(v);
  }
  function reset() {
    queue.reset();
  }
  return {
    getDirection,
    add,
    reset
  };
}
function createVelocityQueue(initOptions) {
  const queue = createQueue();
  let maxTimeWindow = initOptions.maxTimeWindow;
  let minVelocity = initOptions.minVelocity;
  let maxVelocity = initOptions.maxVelocity;
  let multiplicator = initOptions.multiplicator;
  function getVelocity() {
    const length = queue.timeQueue.length;
    if (length < 2) {
      return { x: 0, y: 0 };
    }
    const lastIndex = length - 1;
    const lastPos = queue.queue[lastIndex];
    const lastTime = queue.timeQueue[lastIndex];
    let weightedVelocityX = 0;
    let weightedVelocityY = 0;
    let totalWeight = 0;
    for (let i = lastIndex - 1; i >= 0; i--) {
      const timeDiff = lastTime - queue.timeQueue[i];
      if (timeDiff > maxTimeWindow) {
        break;
      }
      const pos = queue.queue[i];
      const distanceX = lastPos.x - pos.x;
      const distanceY = lastPos.y - pos.y;
      const timeInSeconds = timeDiff / 1e3;
      if (timeInSeconds === 0) {
        continue;
      }
      const weight = 1 - timeDiff / maxTimeWindow;
      weightedVelocityX += distanceX / timeInSeconds * weight;
      weightedVelocityY += distanceY / timeInSeconds * weight;
      totalWeight += weight;
    }
    if (totalWeight === 0) {
      return { x: 0, y: 0 };
    }
    const averageVelocity = {
      x: limit(weightedVelocityX / totalWeight * multiplicator),
      y: limit(weightedVelocityY / totalWeight * multiplicator)
    };
    return {
      x: Math.abs(averageVelocity.x) >= minVelocity ? averageVelocity.x : 0,
      y: Math.abs(averageVelocity.y) >= minVelocity ? averageVelocity.y : 0
    };
  }
  function limit(v) {
    return Math.min(Math.max(v, -maxVelocity), maxVelocity);
  }
  function add(v) {
    queue.add(v);
  }
  function init(options) {
    if (options.maxVelocity) {
      maxVelocity = options.maxVelocity;
    }
    if (options.maxTimeWindow) {
      maxTimeWindow = options.maxTimeWindow;
    }
    if (options.minVelocity) {
      minVelocity = options.minVelocity;
    }
    if (options.multiplicator) {
      multiplicator = options.multiplicator;
    }
    queue.reset();
  }
  return {
    getVelocity,
    add,
    init
  };
}

function prepareForDrag(artboard) {
  const initialOffset = { ...artboard.getOffset() };
  const boundaries = artboard.getBoundaries();
  artboard.cancelAnimation();
  artboard.setInteraction("dragging");
  artboard.setMomentum();
  artboard.setTouchDirection("none");
  const dampedX = dampenRelative(
    initialOffset.x,
    boundaries.xMin,
    boundaries.xMax,
    artboard.options.springDamping
  );
  const dampedY = dampenRelative(
    initialOffset.y,
    boundaries.yMin,
    boundaries.yMax,
    artboard.options.springDamping
  );
  initialOffset.x += (initialOffset.x - dampedX) / artboard.options.springDamping;
  initialOffset.y += (initialOffset.y - dampedY) / artboard.options.springDamping;
  artboard.setOffset(initialOffset.x, initialOffset.y);
  return initialOffset;
}

const MOUSE_BUTTONS = Object.freeze({
  /**
   *No button or un-initialized.
   */
  NONE: 0,
  /**
   * Primary button (usually the left button).
   */
  PRIMARY: 1,
  /**
   * Secondary button (usually the right button).
   */
  SECONDARY: 2,
  /**
   * Auxiliary button (usually the mouse wheel button or middle button).
   */
  AUXILIARY: 4,
  /**
   * 4th button (typically the "Browser Back" button).
   */
  FOURTH: 8,
  /**
   * 5th button (typically the "Browser Forward" button).
   */
  FIFTH: 16
});

const mouse = defineArtboardPlugin(function(artboard, options) {
  const rootEl = artboard.getRootElement();
  let prevFocusedElement = null;
  let isPressingSpace = false;
  const originalCursor = rootEl.style.cursor;
  let initialTouchPoint = null;
  let initialOffset = null;
  function getQueueOptions() {
    const velocityQueueOptions = options.get("velocity", {});
    return {
      maxTimeWindow: velocityQueueOptions.maxTimeWindow || 200,
      maxVelocity: velocityQueueOptions.maxVelocity || 6e3,
      minVelocity: velocityQueueOptions.minVelocity || 300,
      multiplicator: velocityQueueOptions.multiplicator || 1.2
    };
  }
  const velocityQueue = createVelocityQueue(getQueueOptions());
  function onClick(e) {
    const interaction = artboard.getInteraction();
    if (isPressingSpace || interaction === "momentum" || artboard.wasMomentumScrolling()) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  }
  function destroy() {
    window.removeEventListener("pointerup", onPointerUp, {
      capture: true
    });
    rootEl.removeEventListener("pointerdown", onPointerDown, {
      capture: false
    });
    rootEl.removeEventListener("click", onClick, {
      capture: true
    });
    document.removeEventListener("keydown", onKeyDown);
    document.removeEventListener("keyup", onKeyUp);
    document.removeEventListener("pointermove", onPointerMove);
    rootEl.style.cursor = originalCursor;
  }
  function onKeyDown(e) {
    if (artboard.getInteraction() !== "none") {
      e.preventDefault();
      e.stopPropagation();
    }
    if (e.code === "Space") {
      if (!isPressingSpace && options.should("setCursor")) {
        rootEl.style.cursor = "move";
      }
      isPressingSpace = true;
    }
  }
  function onKeyUp(e) {
    if (e.code === "Space") {
      if (isPressingSpace && options.should("setCursor")) {
        rootEl.style.cursor = originalCursor;
      }
      isPressingSpace = false;
      if (prevFocusedElement) {
        prevFocusedElement.focus();
        prevFocusedElement = null;
      }
    }
  }
  function onPointerDown(e) {
    if (e.pointerType !== "mouse") {
      return;
    }
    e.stopPropagation();
    artboard.setMomentum();
    const canDrag = isPressingSpace || !options.should("useSpacebar") || e.buttons === MOUSE_BUTTONS.AUXILIARY;
    if (canDrag) {
      e.preventDefault();
      if (document.activeElement instanceof HTMLElement) {
        prevFocusedElement = document.activeElement;
        document.activeElement.blur();
      }
    }
    document.removeEventListener("pointermove", onPointerMove);
    if (artboard.getInteraction() === "momentum" || canDrag) {
      e.preventDefault();
    }
    if (e.buttons === MOUSE_BUTTONS.PRIMARY && canDrag || e.buttons === MOUSE_BUTTONS.SECONDARY && !isPressingSpace || e.buttons === MOUSE_BUTTONS.AUXILIARY) {
      e.preventDefault();
      velocityQueue.init(getQueueOptions());
      initialOffset = prepareForDrag(artboard);
      initialTouchPoint = getEventCoords(e);
      document.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, {
        capture: true
      });
    }
  }
  function onPointerMove(e) {
    if (e.pointerType !== "mouse" || !initialOffset) {
      return;
    }
    if (!isPressingSpace && options.should("useSpacebar") && e.buttons !== MOUSE_BUTTONS.AUXILIARY) {
      onPointerUp(e);
      return;
    }
    if (!initialTouchPoint) {
      return;
    }
    const coords = getEventCoords(e);
    velocityQueue.add(coords);
    if (artboard.getTouchDirection() === "none") {
      const direction = getDirection(
        coords,
        initialTouchPoint,
        options.get("scrollDirectionThreshold", 20)
      );
      artboard.setTouchDirection(direction);
    }
    artboard.setDirectionOffset(
      initialOffset.x + coords.x - initialTouchPoint.x,
      initialOffset.y + coords.y - initialTouchPoint.y
    );
  }
  function onPointerUp(e) {
    if (e.pointerType !== "mouse") {
      return;
    }
    document.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp, {
      capture: true
    });
    if (!initialTouchPoint) {
      return;
    }
    velocityQueue.add(getEventCoords(e));
    artboard.startMomentum(velocityQueue.getVelocity());
    initialTouchPoint = null;
  }
  rootEl.addEventListener("pointerdown", onPointerDown, {
    passive: false,
    capture: false
  });
  document.addEventListener("keydown", onKeyDown, {
    passive: false
  });
  document.addEventListener("keyup", onKeyUp, {
    passive: false
  });
  rootEl.addEventListener("click", onClick, {
    capture: true
  });
  return {
    options,
    destroy
  };
});

const overview = defineArtboardPlugin(
  function(artboard, options) {
    const overviewEl = options.getRequired("element");
    let overviewWidth = overviewEl.offsetWidth;
    let overviewHeight = overviewEl.offsetHeight;
    const overviewArtboardEl = options.getElement(
      "artboardElement",
      ".artboard-overview-artboard",
      overviewEl
    );
    const overviewVisibleEl = options.getElement(
      "visibleAreaElement",
      ".artboard-overview-visible",
      overviewEl
    );
    const overviewElStyleOverrider = inlineStyleOverrider(overviewEl);
    const artboardElStyleOverrider = inlineStyleOverrider(overviewArtboardEl);
    const visibileEllStyleOverrider = inlineStyleOverrider(overviewVisibleEl);
    let isDragging = false;
    let dragStartCoords = { x: 0, y: 0 };
    let initialVisibleArea = { x: 0, y: 0 };
    let currentArtboardScale = artboard.getScale();
    let currentScale = artboard.getScale();
    let visibleArea = { width: 0, height: 0 };
    const { unobserve } = artboard.observeSizeChange(overviewEl, (entry) => {
      const size = entry?.contentRect;
      if (!size) {
        return;
      }
      overviewWidth = size.width;
      overviewHeight = size.height;
    });
    function destroy() {
      unobserve();
      overviewEl.removeEventListener("pointerdown", onPointerDown);
      overviewEl.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      if (options.should("restoreStyles")) {
        overviewElStyleOverrider.restore();
        visibileEllStyleOverrider.restore();
        artboardElStyleOverrider.restore();
      }
    }
    function applyArtboardStyles(x, y, width, height) {
      artboardElStyleOverrider.setTransform(x, y);
      artboardElStyleOverrider.set("width", width);
      artboardElStyleOverrider.set("height", height);
    }
    function applyVisibleStyles(x, y, width, height) {
      const newX = Math.max(-100, x);
      const newY = Math.max(-100, y);
      visibileEllStyleOverrider.setTransform(newX, newY);
      visibileEllStyleOverrider.set("width", Math.round(width - (newX - x)));
      visibileEllStyleOverrider.set("height", Math.round(height - (newY - y)));
    }
    function getOverviewHeight(autoHeight, artboardSize, padding) {
      if (autoHeight) {
        const artboardAspect = artboardSize.width / artboardSize.height;
        const availableWidth = overviewWidth - padding * 2;
        const height = availableWidth / artboardAspect;
        return height + padding * 2;
      }
      return overviewHeight;
    }
    function loop(ctx) {
      const { rootSize, artboardSize, scale, offset } = ctx;
      if (!artboardSize) {
        throw new Error("Overview plugin can not be used with infinite canvas.");
      }
      const autoHeight = options.should("autoHeight");
      const padding = options.get("padding", 20);
      const minimalAvailableWidth = 1;
      const minimalAvailableHeight = 1;
      let adjustedPaddingX = padding;
      let adjustedPaddingY = padding;
      const totalDesiredPaddingX = padding * 2;
      const totalDesiredPaddingY = padding * 2;
      const actualOverviewHeight = getOverviewHeight(
        autoHeight,
        artboardSize,
        padding
      );
      const maxTotalPaddingX = overviewWidth - minimalAvailableWidth;
      const maxTotalPaddingY = actualOverviewHeight - minimalAvailableHeight;
      if (totalDesiredPaddingX > maxTotalPaddingX) {
        const ratioX = maxTotalPaddingX / totalDesiredPaddingX;
        adjustedPaddingX = Math.max(1, padding * ratioX);
      }
      if (totalDesiredPaddingY > maxTotalPaddingY) {
        const ratioY = maxTotalPaddingY / totalDesiredPaddingY;
        adjustedPaddingY = Math.max(1, padding * ratioY);
      }
      const availableWidth = overviewWidth - adjustedPaddingX * 2;
      const availableHeight = actualOverviewHeight - adjustedPaddingY * 2;
      const artboardAspect = artboardSize.width / artboardSize.height;
      const availableAspect = availableWidth / availableHeight;
      const artboardScale = artboardAspect > availableAspect ? availableWidth / artboardSize.width : availableHeight / artboardSize.height;
      const artboardWidth = artboardSize.width * artboardScale;
      const artboardHeight = artboardSize.height * artboardScale;
      const artboardX = adjustedPaddingX + (availableWidth - artboardWidth) / 2;
      const artboardY = adjustedPaddingY + (availableHeight - artboardHeight) / 2;
      applyArtboardStyles(
        Math.round(artboardX),
        Math.round(artboardY),
        Math.round(artboardWidth),
        Math.round(artboardHeight)
      );
      const newVisibleArea = {
        x: -offset.x / scale,
        y: -offset.y / scale,
        width: rootSize.width / scale,
        height: rootSize.height / scale
      };
      const visibleWidth = newVisibleArea.width * artboardScale;
      const visibleHeight = newVisibleArea.height * artboardScale;
      const visibleX = artboardX + newVisibleArea.x * artboardScale;
      const visibleY = artboardY + newVisibleArea.y * artboardScale;
      applyVisibleStyles(
        Math.round(visibleX),
        Math.round(visibleY),
        Math.round(visibleWidth),
        Math.round(visibleHeight)
      );
      if (autoHeight) {
        overviewElStyleOverrider.set("height", actualOverviewHeight);
      }
      currentArtboardScale = artboardScale;
      currentScale = scale;
      visibleArea = newVisibleArea;
    }
    function isInsideButton(target) {
      if (!(target instanceof HTMLElement) && !(target instanceof SVGElement)) {
        return false;
      }
      let parent = target;
      while (parent) {
        if (parent === overviewVisibleEl) {
          return true;
        } else if (parent === overviewEl) {
          return false;
        }
        parent = parent.parentElement;
      }
      return false;
    }
    function onTouchStart(e) {
      e.preventDefault();
      e.stopPropagation();
    }
    function onPointerDown(e) {
      if (!(e.target instanceof HTMLElement)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      artboard.cancelAnimation();
      artboard.setInteraction("none");
      if (!isInsideButton(e.target)) {
        moveVisibleEl(e);
      }
      isDragging = true;
      dragStartCoords = { x: e.clientX, y: e.clientY };
      const offset = artboard.getOffset();
      initialVisibleArea = {
        x: -offset.x / currentScale,
        y: -offset.y / currentScale
      };
      document.addEventListener("pointermove", onPointerMove, {
        capture: true
      });
      document.addEventListener("pointerup", onPointerUp, {
        capture: true
      });
    }
    function moveVisibleEl(e) {
      if (isDragging) {
        return;
      }
      const rect = overviewArtboardEl.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      const clickXArtboard = clickX / currentArtboardScale;
      const clickYArtboard = clickY / currentArtboardScale;
      const newVisibleAreaX = clickXArtboard - visibleArea.width / 2;
      const newVisibleAreaY = clickYArtboard - visibleArea.height / 2;
      const x = -newVisibleAreaX * currentScale;
      const y = -newVisibleAreaY * currentScale;
      artboard.setOffset(x, y, true);
    }
    function onPointerMove(e) {
      if (!isDragging) {
        return;
      }
      e.stopPropagation();
      e.preventDefault();
      const deltaX = e.clientX - dragStartCoords.x;
      const deltaY = e.clientY - dragStartCoords.y;
      const deltaVisibleAreaX = deltaX / currentArtboardScale;
      const deltaVisibleAreaY = deltaY / currentArtboardScale;
      const newVisibleAreaX = initialVisibleArea.x + deltaVisibleAreaX;
      const newVisibleAreaY = initialVisibleArea.y + deltaVisibleAreaY;
      const x = -newVisibleAreaX * currentScale;
      const y = -newVisibleAreaY * currentScale;
      artboard.setOffset(x, y, true);
    }
    function onPointerUp(e) {
      e.preventDefault();
      e.stopPropagation();
      isDragging = false;
      document.removeEventListener("pointermove", onPointerMove, {
        capture: true
      });
      document.removeEventListener("pointerup", onPointerUp, {
        capture: true
      });
    }
    if (overviewEl.style.position === "static") {
      overviewElStyleOverrider.set("position", "relative");
    }
    artboardElStyleOverrider.setMultiple({
      position: "absolute",
      top: 0,
      left: 0,
      right: "auto",
      bottom: "auto",
      transformOrigin: "0 0",
      pointerEvents: "none",
      overflow: "hidden"
    });
    visibileEllStyleOverrider.setMultiple({
      position: "absolute",
      top: 0,
      left: 0,
      right: "auto",
      bottom: "auto",
      transformOrigin: "0 0",
      cursor: "move"
    });
    overviewEl.addEventListener("pointerdown", onPointerDown);
    overviewEl.addEventListener("touchstart", onTouchStart);
    return {
      options,
      destroy,
      loop
    };
  }
);

const raf = defineArtboardPlugin(function(artboard, options) {
  let rafId = null;
  function loop(currentTime) {
    artboard.loop(currentTime);
    rafId = window.requestAnimationFrame(loop);
  }
  function destroy() {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
  }
  rafId = window.requestAnimationFrame(loop);
  return {
    options,
    destroy
  };
});

const scrollbar = defineArtboardPlugin(function(artboard, options) {
  const elScrollbar = options.get("element");
  const elButton = options.getElement(
    "thumbElement",
    ".artboard-thumb",
    elScrollbar
  );
  const styleOverrider = inlineStyleOverrider(elButton);
  let scrollbarSize = 300;
  let scrollStart = 0;
  let thumbStart = 0;
  let maxOffset = 0;
  let thumbOffset = 0;
  let scrollThumbSize = 0;
  let scrollSize = 0;
  const { unobserve } = artboard.observeSizeChange(elScrollbar, (entry) => {
    const size = entry.contentBoxSize?.[0];
    if (!size) {
      return;
    }
    const orientation = options.get("orientation");
    scrollbarSize = orientation === "y" ? size.blockSize : size.inlineSize;
  });
  function onClickScrollbar(e) {
    e.stopPropagation();
    e.preventDefault();
    const orientation = options.get("orientation");
    if (orientation === "y") {
      if (e.offsetY < thumbOffset) {
        artboard.scrollPageUp();
      } else {
        artboard.scrollPageDown();
      }
      return;
    }
    if (e.offsetX < thumbOffset) {
      artboard.scrollPageLeft();
    } else {
      artboard.scrollPageRight();
    }
  }
  function getOffsetFromThumb(newThumb) {
    const maxThumb = scrollbarSize - scrollThumbSize;
    const newScrollProgress = newThumb / maxThumb;
    const newScrollOffset = newScrollProgress * scrollSize;
    return newScrollOffset + maxOffset;
  }
  function onThumbMouseMove(e) {
    if (e.buttons === MOUSE_BUTTONS.NONE) {
      onMouseUp();
      return;
    }
    const diff = scrollStart - getMouseCoordinate(e);
    const newThumb = Math.max(
      Math.min(-thumbStart + diff, 0),
      -(scrollbarSize - scrollThumbSize)
    );
    const newOffset = getOffsetFromThumb(newThumb);
    const orientation = options.get("orientation");
    if (orientation === "x") {
      artboard.setOffset(newOffset, null, true);
    } else {
      artboard.setOffset(null, newOffset, true);
    }
  }
  function getMouseCoordinate(e) {
    const orientation = options.get("orientation");
    return orientation === "y" ? e.clientY : e.clientX;
  }
  function onMouseUp() {
    document.removeEventListener("pointermove", onThumbMouseMove, {
      capture: true
    });
    document.removeEventListener("pointerup", onMouseUp, {
      capture: true
    });
  }
  function onThumbMouseDown(e) {
    artboard.cancelAnimation();
    artboard.setInteraction();
    e.stopPropagation();
    e.preventDefault();
    e.stopImmediatePropagation();
    scrollStart = getMouseCoordinate(e);
    thumbStart = thumbOffset;
    document.addEventListener("pointerup", onMouseUp, {
      passive: false,
      capture: true
    });
    document.addEventListener("pointermove", onThumbMouseMove, {
      passive: false,
      capture: true
    });
    onThumbMouseMove(e);
  }
  function applyStyles() {
    const scrollThumbSizeRounded = Math.round(scrollThumbSize);
    const orientation = options.get("orientation");
    const property = orientation === "y" ? "height" : "width";
    styleOverrider.set(property, scrollThumbSizeRounded);
    const thumbOffsetRounded = Math.round(thumbOffset);
    const x = orientation === "y" ? 0 : thumbOffsetRounded;
    const y = orientation === "x" ? 0 : thumbOffsetRounded;
    styleOverrider.setTransform(x, y);
  }
  function loop(ctx) {
    const orientation = options.get("orientation");
    const minThumbSize = options.get("minThumbSize", 32);
    const rootSize = orientation === "x" ? ctx.rootSize.width : ctx.rootSize.height;
    const offset = orientation === "x" ? ctx.offset.x : ctx.offset.y;
    const min = orientation === "x" ? ctx.boundaries.xMin : ctx.boundaries.yMin;
    const max = orientation === "x" ? ctx.boundaries.xMax : ctx.boundaries.yMax;
    maxOffset = max;
    scrollSize = maxOffset - min;
    scrollThumbSize = Math.max(
      Math.min(rootSize / scrollSize * rootSize, rootSize * 0.9),
      minThumbSize
    );
    const maxScrollbarSize = scrollbarSize - scrollThumbSize;
    const scrollTop = scrollSize - (offset - min);
    const scrollProgress = 1 - (offset - min) / scrollSize;
    let thumbOffsetRounded = Math.min(Math.max(scrollProgress, 0), 1) * maxScrollbarSize;
    if (scrollProgress < 0) {
      const overscroll = Math.abs(Math.max(scrollTop, -400)) / 400;
      scrollThumbSize = Math.max(
        scrollThumbSize - 100 * overscroll,
        minThumbSize
      );
      thumbOffsetRounded = 0;
    } else if (scrollProgress > 1) {
      const overscroll = Math.min(scrollTop - scrollSize, 400) / 400;
      scrollThumbSize = Math.max(
        scrollThumbSize - 100 * overscroll,
        minThumbSize
      );
      thumbOffsetRounded = scrollbarSize - scrollThumbSize;
    }
    thumbOffset = thumbOffsetRounded;
    applyStyles();
  }
  elButton.addEventListener("pointerdown", onThumbMouseDown, {
    capture: true
  });
  elScrollbar.addEventListener("pointerdown", onClickScrollbar);
  function destroy() {
    unobserve();
    elButton.removeEventListener("pointerdown", onThumbMouseDown, {
      capture: true
    });
    elScrollbar.removeEventListener("pointerdown", onClickScrollbar);
    document.removeEventListener("pointerup", onMouseUp, {
      capture: true
    });
    document.removeEventListener("pointermove", onThumbMouseMove, {
      capture: true
    });
    if (options.should("restoreStyles")) {
      styleOverrider.restore();
    }
  }
  styleOverrider.setMultiple({
    position: "absolute",
    top: 0,
    left: 0,
    bottom: "auto",
    right: "auto"
  });
  return {
    options,
    destroy,
    loop
  };
});

const touch = defineArtboardPlugin(function(artboard, options) {
  const rootEl = artboard.getRootElement();
  let initialTouchDistance = 1;
  let initialScale = 1;
  let initialTouchPoint = null;
  let initialOffset = { x: 0, y: 0 };
  let lastTouch = null;
  let lastScaleTimestamp = 0;
  let scaleMidpoint = null;
  let touchStartDirection = null;
  let touchStartTime = 0;
  const directionQueue = createDirectionQueue({
    threshold: options.get("scrollDirectionThreshold", 20)
  });
  function getQueueOptions() {
    const velocityQueueOptions = options.get("velocity", {});
    return {
      maxTimeWindow: velocityQueueOptions.maxTimeWindow || 210,
      maxVelocity: velocityQueueOptions.maxVelocity || 5e3,
      minVelocity: velocityQueueOptions.minVelocity || 20,
      multiplicator: velocityQueueOptions.multiplicator || 1.35
    };
  }
  const velocityQueue = createVelocityQueue(getQueueOptions());
  function determineTouchStartDirection() {
    if (artboard.getInteraction() !== "momentum") {
      return null;
    }
    const currentDirection = artboard.getTouchDirection();
    if (currentDirection === "vertical" || currentDirection === "horizontal") {
      const velocity = artboard.getMomentum();
      if (velocity) {
        const totalVelocity = Math.abs(velocity.x) + Math.abs(velocity.y);
        if (totalVelocity >= 200) {
          return currentDirection;
        }
      }
    }
    return null;
  }
  function onTouchStart(e) {
    if (e.touches.length === 1 && !options.should("useTwoTouchScrolling")) {
      onDragStart(e.touches);
      velocityQueue.init(getQueueOptions());
      directionQueue.reset();
      const midpoint = getMidpoint(e.touches);
      velocityQueue.add(midpoint);
      directionQueue.add(midpoint);
    }
    if (e.touches.length === 2) {
      velocityQueue.init(getQueueOptions());
      directionQueue.reset();
      initialTouchDistance = getTouchDistance(e.touches);
      initialScale = artboard.getScale();
    }
  }
  function onTouchMove(e) {
    const now = performance.now();
    if (options.should("useTwoTouchScrolling") && e.touches.length !== 2) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (!lastTouch || !initialTouchPoint) {
      onDragStart(e.touches);
      return;
    }
    const scale = artboard.getScale();
    const offset = artboard.getOffset();
    const midpoint = getMidpoint(e.touches);
    const focalPoint = {
      x: (midpoint.x - offset.x) / scale,
      y: (midpoint.y - offset.y) / scale
    };
    velocityQueue.add(midpoint);
    if (e.touches.length >= 2) {
      if (artboard.getInteraction() !== "scaling") {
        artboard.setInteraction("scaling");
        lastTouch = { x: midpoint.x, y: midpoint.y };
      }
      velocityQueue.init(getQueueOptions());
      const newTouchDistance = getTouchDistance(e.touches);
      const scaleFactor = newTouchDistance / initialTouchDistance;
      artboard.setScale(initialScale * scaleFactor);
      const diffX = midpoint.x - lastTouch.x;
      const diffY = midpoint.y - lastTouch.y;
      const updatedScale = artboard.getScale();
      const newOffsetX = midpoint.x - focalPoint.x * updatedScale;
      const newOffsetY = midpoint.y - focalPoint.y * updatedScale;
      scaleMidpoint = {
        x: midpoint.x,
        y: midpoint.y
      };
      artboard.setOffset(newOffsetX + diffX, newOffsetY + diffY, true);
      lastTouch = { x: midpoint.x, y: midpoint.y };
      lastScaleTimestamp = now;
      return;
    }
    artboard.setInteraction("dragging");
    if (artboard.getTouchDirection() === "none") {
      if (touchStartDirection && now - touchStartTime <= 300) {
        artboard.setTouchDirection(touchStartDirection);
      } else {
        directionQueue.add(midpoint);
        const possibleDirection = directionQueue.getDirection(true);
        if (possibleDirection) {
          artboard.setTouchDirection(possibleDirection);
        }
      }
    }
    artboard.setDirectionOffset(
      initialOffset.x + midpoint.x - initialTouchPoint.x,
      initialOffset.y + midpoint.y - initialTouchPoint.y
    );
    lastTouch = { x: midpoint.x, y: midpoint.y };
  }
  function onTouchEnd(e) {
    if (e.changedTouches.length > 2) {
      return;
    }
    const interaction = artboard.getInteraction();
    if (interaction === "dragging" || interaction === "momentum" || interaction === "scaling") {
      e.stopImmediatePropagation();
    }
    if (e.touches.length <= 1 && scaleMidpoint) {
      applyScaleBoundaries({ ...scaleMidpoint });
    }
    if (artboard.getInteraction() === "scaling" && e.touches.length === 1) {
      artboard.setTouchDirection("both");
      artboard.setInteraction("dragging");
      initialTouchPoint = null;
      initialOffset = { ...artboard.getOffset() };
      lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      lastScaleTimestamp = performance.now();
      initialScale = artboard.getScale();
      return;
    }
    if (e.touches.length !== 0) {
      return;
    }
    artboard.setInteraction("none");
    if (!initialTouchPoint || !lastTouch) {
      return;
    }
    initialTouchPoint = null;
    scaleMidpoint = null;
    const timeSinceLastScale = performance.now() - lastScaleTimestamp;
    if (timeSinceLastScale < 200) {
      artboard.startMomentum();
      return;
    }
    const midpoint = getMidpoint(e.changedTouches);
    if (artboard.getTouchDirection() === "none") {
      directionQueue.add(midpoint);
      const possibleDirection = directionQueue.getDirection(true);
      if (possibleDirection) {
        artboard.setTouchDirection(possibleDirection);
      }
    }
    velocityQueue.add(midpoint);
    artboard.startMomentum(velocityQueue.getVelocity());
  }
  function applyScaleBoundaries(midpoint) {
    const scale = artboard.getScale();
    if (scale >= artboard.options.minScale && scale <= artboard.options.maxScale) {
      return;
    }
    const offset = artboard.getOffset();
    const targetX = (midpoint.x - offset.x) / scale;
    const targetY = (midpoint.y - offset.y) / scale;
    const targetScale = scale > artboard.options.maxScale ? artboard.options.maxScale : artboard.options.minScale;
    lastTouch = null;
    initialTouchPoint = null;
    artboard.animateTo(
      "touchScaleBoundaries",
      -targetX * targetScale + midpoint.x,
      -targetY * targetScale + midpoint.y,
      targetScale,
      options.get("overscaleAnimation", {
        duration: 300,
        easing: "easeOutBack"
      })
    );
  }
  function onDragStart(touches) {
    touchStartDirection = determineTouchStartDirection();
    const touch2 = touches[touches.length - 1];
    if (touch2) {
      lastTouch = {
        x: touch2.clientX,
        y: touch2.clientY
      };
      initialTouchPoint = { ...lastTouch };
    }
    touchStartTime = performance.now();
    initialOffset = prepareForDrag(artboard);
    initialScale = artboard.getScale();
    velocityQueue.init(getQueueOptions());
  }
  function destroy() {
    rootEl.removeEventListener("touchstart", onTouchStart);
    rootEl.removeEventListener("touchmove", onTouchMove);
    rootEl.removeEventListener("touchend", onTouchEnd, {
      capture: true
    });
  }
  rootEl.addEventListener("touchstart", onTouchStart, {
    passive: false
  });
  rootEl.addEventListener("touchmove", onTouchMove, {
    passive: false
  });
  rootEl.addEventListener("touchend", onTouchEnd, {
    passive: false,
    capture: true
  });
  return {
    options,
    destroy
  };
});

function isMac() {
  return /Mac|iPod|iPhone|iPad/.test(window.navigator.userAgent);
}

const MAX_ZOOM_STEP = 10;
const BUFFER_SIZE = 10;
const DETECTION_THRESHOLD = 3;
const DIRECTION_LOCK_DURATION = 500;
const CONTRARY_EVENTS_THRESHOLD = 6;
const EVENT_EXPIRY_TIME = 500;
const AXIS_PRIMARY_RATIO = 2.5;
const MIN_DELTA_THRESHOLD = 0.5;
const BOTH_AXIS_MIN_RATIO = 0.2;
class WheelNormalizer {
  eventBuffer = [];
  currentDirection = "none";
  directionLockTimestamp = 0;
  contraryEventCount = 0;
  isMac = isMac();
  /**
   * Process a wheel event and return normalized deltas.
   */
  onWheel(event) {
    let { deltaY, deltaX } = event;
    let deltaZ = 0;
    if (event.ctrlKey || event.altKey || event.metaKey) {
      deltaZ = (Math.abs(deltaY) > MAX_ZOOM_STEP ? MAX_ZOOM_STEP * Math.sign(deltaY) : deltaY) / 100;
    } else {
      if (event.shiftKey && !this.isMac) {
        deltaX = deltaY;
        deltaY = 0;
        this.setDirection("horizontal", event.timeStamp);
      }
    }
    this.eventBuffer = this.eventBuffer.filter(
      (e) => event.timeStamp - e.timestamp < EVENT_EXPIRY_TIME
    );
    this.eventBuffer.push({
      deltaX: Math.abs(event.deltaX) > MIN_DELTA_THRESHOLD ? event.deltaX : 0,
      deltaY: Math.abs(event.deltaY) > MIN_DELTA_THRESHOLD ? event.deltaY : 0,
      timestamp: event.timeStamp
    });
    if (this.eventBuffer.length > BUFFER_SIZE) {
      this.eventBuffer.shift();
    }
    if (this.currentDirection !== "none" && event.timeStamp - this.directionLockTimestamp > DIRECTION_LOCK_DURATION) {
      this.resetDirection();
    }
    if (this.currentDirection === "none") {
      this.detectDirection(event.timeStamp);
    } else {
      this.validateCurrentDirection(event, event.timeStamp);
    }
    return {
      x: this.currentDirection === "vertical" ? 0 : deltaX,
      y: this.currentDirection === "horizontal" ? 0 : deltaY,
      z: deltaZ,
      direction: this.currentDirection
    };
  }
  /**
   * Detect scroll direction based on buffered events
   */
  detectDirection(currentTime) {
    if (this.eventBuffer.length < DETECTION_THRESHOLD) {
      return;
    }
    const recentEvents = this.eventBuffer.slice(-DETECTION_THRESHOLD);
    let totalAbsX = 0;
    let totalAbsY = 0;
    for (const event of recentEvents) {
      totalAbsX += Math.abs(event.deltaX);
      totalAbsY += Math.abs(event.deltaY);
    }
    if (totalAbsX < MIN_DELTA_THRESHOLD && totalAbsY < MIN_DELTA_THRESHOLD) {
      return;
    }
    const maxDelta = Math.max(totalAbsX, totalAbsY);
    const minDelta = Math.min(totalAbsX, totalAbsY);
    const ratio = maxDelta > 0 ? minDelta / maxDelta : 0;
    if (ratio >= BOTH_AXIS_MIN_RATIO) {
      this.setDirection("both", currentTime);
    } else if (totalAbsX > totalAbsY) {
      this.setDirection("horizontal", currentTime);
    } else {
      this.setDirection("vertical", currentTime);
    }
  }
  /**
   * Validate if current direction is still correct or needs re-detection.
   */
  validateCurrentDirection(event, currentTime) {
    const isContrary = this.isContraryEvent(event);
    if (isContrary) {
      this.contraryEventCount++;
      if (this.contraryEventCount >= CONTRARY_EVENTS_THRESHOLD) {
        this.resetDirection();
        this.detectDirection(currentTime);
      }
    } else {
      this.contraryEventCount = 0;
      this.directionLockTimestamp = currentTime;
    }
  }
  /**
   * Check if event goes against current direction.
   */
  isContraryEvent(event) {
    const absX = Math.abs(event.deltaX);
    const absY = Math.abs(event.deltaY);
    switch (this.currentDirection) {
      case "horizontal":
        return absY > absX * AXIS_PRIMARY_RATIO && absY > MIN_DELTA_THRESHOLD;
      case "vertical":
        return absX > absY * AXIS_PRIMARY_RATIO && absX > MIN_DELTA_THRESHOLD;
    }
    return false;
  }
  /**
   * Set the current direction and lock timestamp.
   */
  setDirection(direction, timestamp) {
    this.currentDirection = direction;
    this.directionLockTimestamp = timestamp;
    this.contraryEventCount = 0;
  }
  /**
   * Reset direction to none.
   */
  resetDirection() {
    this.currentDirection = "none";
    this.directionLockTimestamp = 0;
    this.contraryEventCount = 0;
  }
  /**
   * Reset to initial state.
   */
  reset() {
    this.eventBuffer = [];
    this.resetDirection();
  }
}

const wheel = defineArtboardPlugin(function(artboard, options) {
  const rootEl = artboard.getRootElement();
  const IS_MAC = isMac();
  const normalizer = new WheelNormalizer();
  function onWheelRootElement(e) {
    const data = normalizer.onWheel(e);
    if (e.ctrlKey || e.metaKey) {
      artboard.cancelAnimation();
      e.preventDefault();
      e.stopPropagation();
      doZoom(e.pageX, e.pageY, data.z * -1);
      return;
    }
    const scrollSpeed = options.get("scrollSpeed", 1);
    e.preventDefault();
    e.stopPropagation();
    const useMomentumScroll = options.should("useMomentumScroll");
    if (useMomentumScroll && !IS_MAC && data.direction !== "both") {
      const velocity = artboard.getMomentum() || { x: 0, y: 0 };
      artboard.setInteraction("momentum");
      artboard.setMomentum(
        velocity.x - data.x * scrollSpeed * 9.6,
        velocity.y - data.y * scrollSpeed * 9.6,
        0.9
      );
    } else {
      const offset = artboard.getOffset();
      artboard.cancelAnimation();
      artboard.setInteraction("none");
      artboard.setOffset(
        offset.x + -(data.x * scrollSpeed),
        offset.y + -(data.y * scrollSpeed),
        !useMomentumScroll
      );
    }
  }
  function doZoom(x, y, delta) {
    const wheelZoomFactor = options.get("wheelZoomFactor", 0.8);
    const scaleFactor = Math.exp(delta * wheelZoomFactor);
    if (options.should("useMomentumZoom")) {
      const currentTarget = artboard.getInteraction() === "momentumScaling" ? artboard.getScaleTarget() : void 0;
      const newScale = (currentTarget?.scale || artboard.getScale()) * scaleFactor;
      const offsetToUse = currentTarget ? { x: currentTarget.x, y: currentTarget.y } : void 0;
      const velocity = artboard.calculateScaleAroundPoint(
        x,
        y,
        newScale,
        offsetToUse,
        currentTarget?.scale
      );
      artboard.setScaleTarget(velocity.x, velocity.y, velocity.scale);
      artboard.setInteraction("momentumScaling");
    } else {
      const newScale = artboard.getScale() * scaleFactor;
      artboard.scaleAroundPoint(x, y, newScale);
    }
  }
  function onWheelDocument(e) {
    if (!options.should("interceptWheel")) {
      return;
    }
    onWheelRootElement(e);
  }
  document.addEventListener("wheel", onWheelDocument, {
    passive: false
  });
  rootEl.addEventListener("wheel", onWheelRootElement, {
    passive: false
  });
  function destroy() {
    rootEl.removeEventListener("wheel", onWheelRootElement);
    document.removeEventListener("wheel", onWheelDocument);
  }
  return {
    options,
    destroy
  };
});

const sticky = defineArtboardPlugin(function(artboard, options) {
  const target = options.getRequired("target");
  const style = target instanceof HTMLElement ? inlineStyleOverrider(target) : null;
  const size = target instanceof HTMLElement ? { width: target.offsetWidth, height: target.offsetHeight } : target;
  const coords = { x: 0, y: 0 };
  const observer = target instanceof HTMLElement ? artboard.observeSizeChange(target, (entry) => {
    const newSize = entry.borderBoxSize?.[0];
    if (!newSize) return;
    size.width = newSize.inlineSize;
    size.height = newSize.blockSize;
  }) : null;
  const computed = options.computed(function(o) {
    const originOption = o.origin || "top-left";
    const origin = parseOrigin(originOption);
    const margin = parseEdges(o.margin);
    const positionOption = o.position || "top-left";
    const position = typeof positionOption === "string" ? { type: "origin", ...parseOrigin(positionOption) } : { type: "position", ...positionOption };
    const transformOrigin = originOption.replace("-", " ");
    let xMargin = 0;
    if (origin.x === 0) {
      xMargin += margin.left;
    } else if (origin.x === 1) {
      xMargin -= margin.right;
    }
    let yMargin = 0;
    if (origin.y === 0) {
      yMargin += margin.top;
    } else if (origin.y === 1) {
      yMargin -= margin.bottom;
    }
    return {
      xMargin,
      yMargin,
      margin,
      origin,
      position,
      transformOrigin
    };
  });
  function loop(ctx) {
    if (!options.should("enabled", true)) {
      return;
    }
    if (!(target instanceof HTMLElement)) {
      const newSize = options.get("target");
      size.width = newSize.width;
      size.height = newSize.height;
    }
    const artboardWidth = (ctx.artboardSize?.width || 0) * ctx.scale;
    const artboardHeight = (ctx.artboardSize?.height || 0) * ctx.scale;
    const keepVisible = options.should("keepVisible");
    let x = computed.value.position.type === "origin" ? computed.value.position.x * artboardWidth : computed.value.position.x * ctx.scale;
    let y = computed.value.position.type === "origin" ? computed.value.position.y * artboardHeight : computed.value.position.y * ctx.scale;
    x += ctx.offset.x + computed.value.xMargin - size.width * computed.value.origin.x;
    y += ctx.offset.y + computed.value.yMargin - size.height * computed.value.origin.y;
    if (keepVisible) {
      x = Math.min(
        Math.max(x, computed.value.margin.left),
        ctx.rootSize.width - size.width - computed.value.margin.right
      );
      y = Math.min(
        Math.max(y, computed.value.margin.top),
        ctx.rootSize.height - size.height - computed.value.margin.bottom
      );
    }
    const precision = options.get("precision", 0.5);
    coords.x = withPrecision(x, precision);
    coords.y = withPrecision(y, precision);
    if (style) {
      style.setTransform(coords.x, coords.y);
      style.set("transformOrigin", computed.value.transformOrigin);
    }
  }
  function destroy() {
    if (observer) {
      observer.unobserve();
    }
    if (options.should("restoreStyles") && style) {
      style.restore();
    }
  }
  if (style) {
    style.setMultiple({
      position: "absolute",
      top: "0",
      left: "0",
      right: "auto",
      bottom: "auto"
    });
  }
  function getRect() {
    return {
      ...size,
      ...coords
    };
  }
  return {
    options,
    destroy,
    loop,
    getRect
  };
});

export { clickZoom, createArtboard, cssProperties, defineArtboardPlugin, dom, doubleTapZoom, inlineStyleOverrider, keyboard, mouse, overview, raf, scrollbar, sticky, touch, wheel };
